import React, { useState, useEffect } from "react";
import NavigationBar from "../../components/navbar/NavigationBar";
import styles from "./Quickstart.module.css";
import GitHubIcon from '@mui/icons-material/GitHub';
import KeyIcon from '@mui/icons-material/Key';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

// Try to import fetched data, fallback to hardcoded
let fetchedData = null;
try {
    fetchedData = require('../../data/quickstart-examples.json');
} catch (e) {
    console.warn('Using hardcoded examples, fetched data not available');
}

// Hardcoded fallback examples
const fallbackExamples = {
    ios: {
        installation: `1. File → "Add Package Dependencies..."
2. Enter URL: https://github.com/Presage-Security/SmartSpectra
3. Select "Branch" → "main"
4. Add to your target`,
        implementation: `import SmartSpectra

let vitals = SmartSpectra()
vitals.startMeasurement { result in
    switch result {
    case .success(let measurements):
        print("Heart Rate: \\(measurements.heartRate) BPM")
        print("Breathing Rate: \\(measurements.breathingRate) RPM")
    case .failure(let error):
        print("Error: \\(error)")
    }
}`
    },
    android: {
        installation: `implementation 'com.presagetech:smartspectra:+'

// This will use the latest version available
// Check specific versions at: https://mvnrepository.com/artifact/com.presagetech/smartspectra`,
        implementation: `import com.presagetech.smartspectra.SmartSpectra;

SmartSpectra vitals = new SmartSpectra("your-api-key");
vitals.startMeasurement(new MeasurementCallback() {
    @Override
    public void onSuccess(VitalMeasurements measurements) {
        Log.d("Vitals", "Heart Rate: " + measurements.getHeartRate() + " BPM");
        Log.d("Vitals", "Breathing Rate: " + measurements.getBreathingRate() + " RPM");
    }
    
    @Override
    public void onError(Exception error) {
        Log.e("Vitals", "Measurement failed", error);
    }
});`
    },
    cpp: {
        installation: `curl -s "https://presage-security.github.io/PPA/KEY.gpg" | gpg --dearmor | sudo tee /etc/apt/trusted.gpg.d/presage-technologies.gpg >/dev/null
sudo curl -s --compressed -o /etc/apt/sources.list.d/presage-technologies.list "https://presage-security.github.io/PPA/presage-technologies.list"
sudo apt update
sudo apt install libsmartspectra-dev`,
        implementation: `#include <smartspectra/vitals.h>
#include <iostream>

int main() {
    SmartSpectra::VitalsSDK vitals("your-api-key");
    
    auto measurements = vitals.measureVitals();
    if (measurements.has_value()) {
        std::cout << "Heart Rate: " << measurements->heartRate << " BPM" << std::endl;
        std::cout << "Breathing Rate: " << measurements->breathingRate << " RPM" << std::endl;
    } else {
        std::cerr << "Measurement failed" << std::endl;
    }
    
    return 0;
}`
    }
};

function Quickstart() {
    const [activeTab, setActiveTab] = useState('ios');
    const [copiedCode, setCopiedCode] = useState(null);
    const [docData, setDocData] = useState(null);

    useEffect(() => {
        if (fetchedData) {
            setDocData(fetchedData);
        }
    }, []);

    const handleCopyCode = async (code, id) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(id);
            setTimeout(() => setCopiedCode(null), 2000);
        } catch (error) {
            // Fallback for older browsers or HTTPS requirement
            const textArea = document.createElement('textarea');
            textArea.value = code;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedCode(id);
                setTimeout(() => setCopiedCode(null), 2000);
            } catch (fallbackError) {
                console.error('Fallback copy failed:', fallbackError);
            }
            document.body.removeChild(textArea);
        }
    };

    const getInstallationContent = (platform) => {
        if (docData?.platforms?.[platform]) {
            const platformData = docData.platforms[platform];
            if (platform === 'cpp' && platformData.installation?.codeBlocks?.length > 0) {
                return platformData.installation.codeBlocks[0].code;
            } else if (platform === 'swift' && platformData.quickstart?.raw) {
                // Extract installation from Swift quickstart
                const installMatch = platformData.quickstart.raw.match(/```bash\n([\s\S]*?)```/);
                return installMatch ? installMatch[1] : fallbackExamples.ios.installation;
            } else if (platform === 'android' && platformData.quickstart?.raw) {
                // Extract gradle dependency from Android quickstart
                const gradleMatch = platformData.quickstart.raw.match(/```gradle\n([\s\S]*?)```/);
                return gradleMatch ? gradleMatch[1] : fallbackExamples.android.installation;
            }
        }
        return fallbackExamples[platform === 'swift' ? 'ios' : platform]?.installation || '';
    };

    const getQuickStartInstructions = (platform) => {
        if (docData?.platforms?.[platform]) {
            const platformData = docData.platforms[platform];
            
            if (platform === 'cpp') {
                // Format C++ run instructions
                return `# Run the example application:
rest_continuous_example \\
  --also_log_to_stderr \\
  --camera_device_index=0 \\
  --auto_lock=false \\
  --api_key=YOUR_API_KEY_HERE`;
            } else if (platform === 'swift' && platformData.quickstart?.steps) {
                // Format Swift quick start steps (excluding the clone step)
                const steps = platformData.quickstart.steps
                    .slice(1) // Skip the clone step
                    .filter(step => step.text && !step.text.includes('Clone the repository'))
                    .map((step, index) => `${index + 1}. ${step.text}`)
                    .join('\n');
                return steps || fallbackExamples.ios.implementation;
            } else if (platform === 'android' && platformData.quickstart?.steps) {
                // Format Android quick start steps (excluding the clone step)
                const steps = platformData.quickstart.steps
                    .slice(1) // Skip the clone step
                    .filter(step => step.text && !step.text.includes('Clone the repository'))
                    .map((step, index) => `${index + 1}. ${step.text}`)
                    .join('\n');
                return steps || fallbackExamples.android.implementation;
            }
        }
        return fallbackExamples[platform === 'swift' ? 'ios' : platform]?.implementation || '';
    };

    const tabs = [
        { id: 'ios', label: 'iOS (Swift)', icon: <AppleIcon className={styles["tab-icon"]} /> },
        { id: 'android', label: 'Android', icon: <AndroidIcon className={styles["tab-icon"]} /> },
        { id: 'cpp', label: 'C++', icon: <CodeIcon className={styles["tab-icon"]} /> }
    ];

    return (
        <div className={styles["quickstart-container"]}>
            <NavigationBar />
            
            <div className={styles["quickstart-content"]}>
                <div className={styles["quickstart-header"]}>
                    <h1>Quickstart Guide</h1>
                    <p className={styles["quickstart-subtitle"]}>
                        Get up and running with real-time vitals measurement in under 10 minutes
                    </p>
                    {docData?.lastUpdated && (
                        <p className={styles["last-updated"]}>
                            Documentation synced from GitHub • Last updated: {new Date(docData.lastUpdated).toLocaleDateString()}
                        </p>
                    )}
                </div>

                {/* API Key Section */}
                <div className={styles["api-key-section"]}>
                    <div className={styles["step"]}>
                        <div className={styles["step-number"]}>1</div>
                        <div className={styles["step-content"]}>
                            <h3>Get Your API Key</h3>
                            <p>Sign up for a free account to get your API key with 100 free measurements per month.</p>
                            <button 
                                className={styles["api-key-button"]}
                                onClick={() => window.location.href = '/auth/register'}
                            >
                                <KeyIcon className={styles["button-icon"]} />
                                Get Free API Key
                            </button>
                        </div>
                    </div>
                </div>

                {/* Platform Tabs */}
                <div className={styles["tabs-container"]}>
                    <div className={styles["tabs-header"]}>
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`${styles["tab-button"]} ${activeTab === tab.id ? styles["tab-active"] : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className={styles["tab-content"]}>
                        {/* Installation Section */}
                        <div className={styles["step"]}>
                            <div className={styles["step-number"]}>2</div>
                            <div className={styles["step-content"]}>
                                <h3>Installation</h3>
                                <div className={styles["code-block-container"]}>
                                    <div className={styles["code-header"]}>
                                        <span className={styles["code-label"]}>
                                            {activeTab === 'ios' && 'Swift Package Manager'}
                                            {activeTab === 'android' && 'Gradle (build.gradle)'}
                                            {activeTab === 'cpp' && 'Ubuntu 22.04 (Only Supported Platform)'}
                                        </span>
                                        <button
                                            className={styles["copy-button"]}
                                            onClick={() => handleCopyCode(
                                                getInstallationContent(activeTab === 'ios' ? 'swift' : activeTab),
                                                `install-${activeTab}`
                                            )}
                                        >
                                            {copiedCode === `install-${activeTab}` ? (
                                                <>
                                                    <CheckIcon className={styles["copy-icon"]} />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <ContentCopyIcon className={styles["copy-icon"]} />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <pre className={styles["code-block"]}>
                                        <code>{getInstallationContent(activeTab === 'ios' ? 'swift' : activeTab)}</code>
                                    </pre>
                                    {activeTab === 'android' && (
                                        <div className={styles["version-note"]}>
                                            <p>
                                                📦 View available versions on{' '}
                                                <a 
                                                    href="https://mvnrepository.com/artifact/com.presagetech/smartspectra" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#c91e1e', textDecoration: 'underline' }}
                                                >
                                                    Maven Repository
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                    {activeTab === 'cpp' && (
                                        <div className={styles["version-note"]}>
                                            <p style={{ color: '#e67e22' }}>
                                                ⚠️ <strong>Note:</strong> Ubuntu 22.04 LTS is currently the only supported platform for the C++ SDK.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quick Start Instructions */}
                        <div className={styles["step"]}>
                            <div className={styles["step-number"]}>3</div>
                            <div className={styles["step-content"]}>
                                <h3>Quick Start Instructions</h3>
                                <div className={styles["code-block-container"]}>
                                    <div className={styles["code-header"]}>
                                        <span className={styles["code-label"]}>
                                            {activeTab === 'ios' && 'iOS Setup Steps'}
                                            {activeTab === 'android' && 'Android Setup Steps'}
                                            {activeTab === 'cpp' && 'Run Example'}
                                        </span>
                                        <button
                                            className={styles["copy-button"]}
                                            onClick={() => handleCopyCode(
                                                getQuickStartInstructions(activeTab === 'ios' ? 'swift' : activeTab),
                                                `impl-${activeTab}`
                                            )}
                                        >
                                            {copiedCode === `impl-${activeTab}` ? (
                                                <>
                                                    <CheckIcon className={styles["copy-icon"]} />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <ContentCopyIcon className={styles["copy-icon"]} />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <pre className={styles["code-block"]}>
                                        <code>{getQuickStartInstructions(activeTab === 'ios' ? 'swift' : activeTab)}</code>
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Expected Output */}
                        <div className={styles["step"]}>
                            <div className={styles["step-number"]}>4</div>
                            <div className={styles["step-content"]}>
                                <h3>Quick Start Instructions Finished</h3>
                                <p>Running app as seen in the gif file below:</p>
                                {activeTab === 'ios' && (
                                    <div className={styles["gif-container"]}>
                                        <img 
                                            src="/images/ios-quickstart.gif" 
                                            alt="iOS Quickstart Demo" 
                                            className={styles["demo-gif"]}
                                            loading="eager"
                                        />
                                    </div>
                                )}
                                {activeTab === 'android' && (
                                    <div className={styles["gif-container"]}>
                                        <img 
                                            src="/images/android-quickstart.gif" 
                                            alt="Android Quickstart Demo" 
                                            className={styles["demo-gif"]}
                                            loading="eager"
                                        />
                                    </div>
                                )}
                                {activeTab === 'cpp' && (
                                    <div className={styles["gif-container"]}>
                                        <img 
                                            src="/images/cpp-quickstart.gif" 
                                            alt="C++ Quickstart Demo" 
                                            className={styles["laptop-gif"]}
                                            loading="eager"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Next Steps */}
                <div className={styles["next-steps"]}>
                    <h2>Next Steps</h2>
                    <div className={styles["next-steps-grid"]}>
                        <div className={styles["next-step-card"]}>
                            <h4>Full Documentation</h4>
                            <p>Explore advanced features, customization options, and all available measurements.</p>
                            <button 
                                className={styles["docs-button"]}
                                onClick={() => window.open('https://docs.physiology.presagetech.com', '_blank')}
                            >
                                View Full Docs
                            </button>
                        </div>
                        
                        <div className={styles["next-step-card"]}>
                            <h4>GitHub Repository</h4>
                            <p>Access sample apps, code examples, and contribute to our open-source SDKs.</p>
                            <button 
                                className={styles["github-button"]}
                                onClick={() => window.open('https://github.com/Presage-Security/SmartSpectra', '_blank')}
                            >
                                <GitHubIcon className={styles["button-icon"]} />
                                View on GitHub
                            </button>
                        </div>
                        
                        <div className={styles["next-step-card"]}>
                            <h4>Get Support</h4>
                            <p>Join our developer community or reach out for technical assistance.</p>
                            <button 
                                className={styles["support-button"]}
                                onClick={() => window.location.href = 'mailto:support@presagetechnologies.com'}
                            >
                                Contact Support
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Quickstart;